
/////////////////main code //////////////////

import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { ArrowLeft, Search, User, X, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, RefreshCcw, ArrowBigRight, } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getTemplateDataPaginated } from '../../services/apiService';
import { decodedToken, removeAuthToken } from '../../utils/auth';

const FilterContent = memo(
  ({
    draftFilters,
    setDraftFilters,
    appliedFilters,
    setAppliedFilters,
    filterFields,
    getUniqueValues,
    resetFilters,
    isApplyDisabled,
    hasActiveFilters,
  }) => {
    const emptyFilters = {
      customerBrand: '',
      productCategory: '',
      level: '',
      projectRanking: '',
      supplier: '',
      status: '',
      certificationlogo: '',
      searchKeyword: '',
    };

    const handleKeywordChange = (e) => {
      const value = e.target.value;
      const sanitized = value.replace(/[^a-zA-Z0-9]/g, '');
      if (sanitized.length <= 20) {
        setDraftFilters((prev) => ({
          ...prev,
          searchKeyword: sanitized,
        }));
      }
    };

    return (
      <>
        {/* Header */}
        <div className="relative flex items-center justify-center mb-6 md:mb-5 md:mt-8">
          <div className="flex items-center gap-2.5 text-2xl font-semibold">
            <Search size={22} className="text-blue-400 md:text-white" />
            <span>Filters</span>
          </div>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="md:hidden absolute right-0 text-sm text-gray-400 hover:text-gray-200 flex items-center gap-1"
            >
              <X size={14} /> 
            </button>
          )}
        </div>

        {/* Filter selects - improved styling */}
        <div className="space-y-4 px-4 md:px-0">
          {filterFields.map(({ key, label, staticOptions }) => (
            <div key={key} className="relative">
              <select
                value={draftFilters[key] || ''}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, [key]: e.target.value }))
                }
                className={`
                  w-full bg-white text-black border border-gray-300  px-4 py-2.5
                  appearance-none cursor-pointer
                  hover:bg-blue-300 focus:bg-blue-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                  transition-all duration-200 shadow-sm
                  pr-10
                `}
              >
                <option value="" className="text-gray-500">
                  {label}
                </option>
                {(staticOptions || getUniqueValues(key)).map((val) => (
                  <option
                    key={val}
                    value={val}
                    className="bg-white text-black checked:bg-blue-600 checked:text-white"
                  >
                    {val}
                  </option>
                ))}
              </select>

              {/* Custom arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                <svg
                  className="h-5 w-5 text-gray-500 transition-transform duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{
                    transform: draftFilters[key] ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Keyword search - restricted input */}
        <div className="mt-6 px-4 md:px-0">
          <input
            type="text"
            placeholder="Search by Keyword"
            value={draftFilters.searchKeyword || ''}
            onChange={handleKeywordChange}
            maxLength={20}
            className="w-full bg-white text-black border border-gray-300  px-4 py-2.5
                     placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                     hover:bg-blue-50 transition-all shadow-sm"
          />
          <p className="mt-1 text-xs text-white">
            Letters and numbers only (≤20 chars)
          </p>
        </div>

        {/* Action buttons */}
        <div className="mt-6 px-4 md:px-0 flex items-center justify-between md:justify-start gap-3 md:gap-4">
          <button
            onClick={() => {
              if (isApplyDisabled) return;
              setDraftFilters(emptyFilters);
              setAppliedFilters(emptyFilters);
            }}
            disabled={isApplyDisabled}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold
                     text-white transition-colors
                     ${
                       isApplyDisabled
                         ? 'bg-gray-400 cursor-not-allowed opacity-70'
                         : 'bg-blue-600 hover:bg-blue-700'
                     }`}
          >
            Reset
            <RefreshCcw size={16} />
          </button>

          <button
            onClick={() => {
              if (isApplyDisabled) return;
              setAppliedFilters({ ...draftFilters });
            }}
            disabled={isApplyDisabled}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold
                     text-white  transition-colors
                     ${
                       isApplyDisabled
                         ? 'bg-gray-400 cursor-not-allowed opacity-70'
                         : 'bg-blue-600 hover:bg-blue-700'
                     }`}
          >
            Apply
            <ArrowBigRight size={18} />
          </button>
        </div>
      </>
    );
  }
);

FilterContent.displayName = 'FilterContent';

// Main component
const MonitorControlTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekInfo, setWeekInfo] = useState({ week: '', date: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [username, setUsername] = useState('');
  const [uploaderInfo, setUploaderInfo] = useState(null);

  // Pagination
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);

  const syncTopScroll = () => {
  if (tableScrollRef.current && topScrollRef.current) {
    tableScrollRef.current.scrollLeft =
      topScrollRef.current.scrollLeft;
  }
};

const syncTableScroll = () => {
  if (tableScrollRef.current && topScrollRef.current) {
    topScrollRef.current.scrollLeft =
      tableScrollRef.current.scrollLeft;
  }
};

  const TEMPLATE_CATEGORY = 'Certification';
  const TEMPLATE_SUB_CATEGORY = 'MNT Control Table';

  const [stats, setStats] = useState({
    finished: 0,
    ongoing: 0,
    overdue: 0,
  });

  const [draftFilters, setDraftFilters] = useState({
    customerBrand: '',
    productCategory: '',
    level: '',
    projectRanking: '',
    supplier: '',
    status: '',
    certificationlogo: '',
    searchKeyword: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({ ...draftFilters });

  const filterFields = [
    { key: 'customerBrand', label: 'Customer Brand' },
    { key: 'productCategory', label: 'Product Category' },
    { key: 'level', label: 'Level' },
    { key: 'projectRanking', label: 'Project Ranking' },
    { key: 'supplier', label: 'Supplier' },
    {
      key: 'status',
      label: 'Status',
      staticOptions: ['Closed', 'On-Going'],
    },
//     {
//     key: 'certificationlogo', label: 'Certification Logo',               
//   },
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const user = decodedToken();
    if (user) {
      setUsername(user.userId || user.name || user.email || 'Unknown');
    }
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchPage(1);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPage = async (page) => {
    try {
      setLoading(true);

      const result = await getTemplateDataPaginated(
        TEMPLATE_CATEGORY,
        TEMPLATE_SUB_CATEGORY,
        page,
        itemsPerPage
      );

      if (!result?.success) {
        const msg = result?.message || 'Failed to fetch data';
        setError(msg);
        Swal.fire({
          icon: 'error',
          title: 'Loading Failed',
          text: msg,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000,
        });
        return;
      }

      const processed = processTemplateData(result.data || []);

      setData(processed);

      if (!hasActiveFilters) {
        setFilteredData(processed);
      }

      setTotalProjects(result.pagination?.totalProjects ?? 0);
      setTotalPages(result.pagination?.totalPages ?? 0);
      setHasNextPage(result.pagination?.hasNextPage ?? false);
      setHasPreviousPage(result.pagination?.hasPreviousPage ?? false);
      setCurrentPage(result.pagination?.currentPage ?? page);

      if (result.statistics) {
        setStats({
          finished: result.statistics.finished || 0,
          ongoing: result.statistics.ongoing || 0,
          overdue: result.statistics.overdue || 0,
        });
      }

      if (result.template) {
        const template = result.template;
        const weekMatch = template.fileVersion?.match(/V?(\d+\.\d+)/);
        setWeekInfo({
          week: weekMatch ? weekMatch[1] : template.fileVersion || 'N/A',
          date: template.uploadDate
            ? new Date(template.uploadDate).toLocaleDateString('en-IN')
            : '',
        });

        const uploader =
          template.uploadedBy ||
          template.uploaderEmail ||
          template.editedBy ||
          template.user ||
          template.uploaded_by ||
          template.email ||
          'Unknown User';

        setUploaderInfo({
          nameOrEmail: uploader,
          uploadDate: template.uploadDate || template.lastModified || null,
          fileVersion: template.fileVersion || 'N/A',
        });
      }
    } catch (err) {
      const msg = err.message?.includes('network')
        ? 'Network error. Please check your connection.'
        : 'Something went wrong while loading data.';
      setError(msg);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: msg,
        toast: true,
        position: 'top-end',
        timer: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const processTemplateData = (templateData) => {
    if (templateData.projects && Array.isArray(templateData.projects)) {
      return templateData.projects.map((project) => {
        const getValue = (val) => (val == null ? '-' : val);

        return {
          customerBrand: project['Customer Brand'],
          customerModel: project['Customer Model Name'],
          productCategory: project['Product Category'],
          level: project['Level'],
          projectRanking: project['Project Ranking'],
          supplier: project['Supplier'],
          koDate: project['KO Date'],
          mp1Deadline: project['MP1 Deadline'],
          isOverdue: project._isOverdue || false,
          scheduleStatus: project['Schedule Plan Status'],
          plan: {
            status: project['Status'],
            hdmi: getValue(project['HDMI']),
            dp: getValue(project['DP']),
            usbIF: getValue(project['USB IF']),
            typeC: getValue(project['typeC 相容性']),
            lbl: getValue(project['LBL']),
            dts: getValue(project['DTS']),
            hdr: getValue(project['HDR']),
            clearMR: getValue(project['Clear MR']),
            tbt4: getValue(project['TBT4']),
            calmanReady: getValue(project['Calman ready']),
            flickerFree: getValue(project['Flicker Free']),
            msDynamic: getValue(project['MS dynamic lighting']),
            helloWebcam: getValue(project['Hello Webcam']),
            circularPolarizer: getValue(project['圆偏光']),
          },
          actual: project['Tracking']?.includes('Actual') ? {} : null,
        };
      });
    }
    return [];
  };

  useEffect(() => {
    let result = [...data];

    if (appliedFilters.customerBrand) {
      result = result.filter((item) =>
        item.customerBrand?.toLowerCase().includes(appliedFilters.customerBrand.toLowerCase())
      );
    }
    if (appliedFilters.productCategory) {
      result = result.filter((item) =>
        item.productCategory?.toLowerCase().includes(appliedFilters.productCategory.toLowerCase())
      );
    }
    if (appliedFilters.level) {
      result = result.filter((item) =>
        item.level?.toLowerCase().includes(appliedFilters.level.toLowerCase())
      );
    }
    if (appliedFilters.projectRanking) {
      result = result.filter((item) =>
        item.projectRanking?.toLowerCase().includes(appliedFilters.projectRanking.toLowerCase())
      );
    }
    if (appliedFilters.supplier) {
      result = result.filter((item) =>
        item.supplier?.toLowerCase().includes(appliedFilters.supplier.toLowerCase())
      );
    }
    if (appliedFilters.status) {
      result = result.filter((item) => {
        const planStatus = item.plan?.status;
        const actualStatus = item.actual?.status;
        return planStatus === appliedFilters.status || actualStatus === appliedFilters.status;
      });
    }
    if (appliedFilters.searchKeyword?.trim()) {
      const kw = appliedFilters.searchKeyword.toLowerCase().trim();
      result = result.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(kw))
      );
    }
    if (appliedFilters.searchKeyword?.trim()) {
      const kw = appliedFilters.searchKeyword.toLowerCase().trim();
      result = result.filter((item) =>
        // Only search in Customer Model Name
        item.customerModel?.toLowerCase().includes(kw)
      );
    }

    setFilteredData(result);

    if (
      Object.values(appliedFilters).some((v) => !!v?.trim()) &&
      result.length === 0 &&
      data.length > 0
    ) {
            Swal.fire({
        html: `
      <div class="inline-block border border-gray-400 rounded bg-white
                  w-[260px] sm:w-[360px]
                  max-w-[90vw] overflow-hidden
                  -translate-x-2 sm:translate-x-0">

        <div class="bg-gray-500 text-center text-sm font-bold px-3 py-1 border-b border-gray-400 text-black">
          Message
        </div>

        <div class="px-5 py-4 flex flex-col items-center gap-3">
          <div class="text-red-600 text-2xl">⚠️</div>

          <div class="text-sm text-gray-700 text-center leading-relaxed">
            No match found.
            Please try again.
          </div>

          <button
            id="swal-close-btn"
            class="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm px-5 py-1.5 rounded"
          >
            Close
          </button>
        </div>
      </div>
    `,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        width: '100%',
        padding: 0,
        background: 'transparent',
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) {
            container.style.overflowX = 'hidden';
          }

          document
            .getElementById('swal-close-btn')
            ?.addEventListener('click', () => {
              Swal.close();
              resetFilters();
            });
        },
      });
    }
  }, [appliedFilters]);

  const resetFilters = () => {
    const empty = {
      customerBrand: '',
      productCategory: '',
      level: '',
      projectRanking: '',
      supplier: '',
      status: '',
      certificationlogo: '',
      searchKeyword: '',
    };
    setDraftFilters(empty);
    setAppliedFilters(empty);
  };

  const getUniqueValues = (key) =>
    [...new Set(data.map((item) => item[key] || ''))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

  const handlePageChange = async (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      await fetchPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getVisiblePageButtons = () => {
    const pagesToShow = 5;
    let start = Math.max(1, currentPage - Math.floor(pagesToShow / 2));
    let end = Math.min(totalPages, start + pagesToShow - 1);

    if (end - start + 1 < pagesToShow) {
      start = Math.max(1, end - pagesToShow + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => {
      const page = start + i;
      return (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          disabled={loading}
          className={`min-w-[36px] h-9 rounded flex items-center justify-center text-sm font-medium transition border border-gray-600 ${
            currentPage === page
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800 hover:bg-gray-700 text-gray-200'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {page}
        </button>
      );
    });
  };

  const hasActiveFilters = Object.values(draftFilters).some((v) => !!v?.trim());
  const filtersAreApplied = Object.values(appliedFilters).some(v => !!v?.trim());

  const isApplyDisabled = !Object.values(draftFilters).some(
    (v) => v && v.toString().trim() !== ''
  );

  if (loading && data.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-white text-lg">Loading certification tracking data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-xl p-8 max-w-lg text-center border border-red-900/30">
          <div className="text-red-400 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-3 text-white">Failed to load data</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={fetchInitialData}
            className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-lg font-medium text-white"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* HEADER */}
      <header
        className="
          sticky top-0 z-30 bg-black border-b border-white
          px-4 py-2 flex flex-col items-start gap-2
          sm:flex-row sm:items-center sm:justify-between sm:gap-3
        "
      >
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate('/Certification')} className="flex items-center shrink-0">
            <ArrowLeft className="scale-75 sm:scale-90 md:scale-100" size={32} />
          </button>
          <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold whitespace-nowrap truncate">
            WK{weekInfo.week} Weekly Report
          </h1>
        </div>

        <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
          <User className="w-5 h-5 text-white" />
          <div className="flex flex-col items-start leading-tight">
            <span className="text-sm font-medium">{username || 'Current User'}</span>
          </div>
        </div>
      </header>

      {/* STATS */}
      <div className="px-4 sm:px-6 md:px-4 py-4 md:py-3 border-b border-white bg-gray-950">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 md:gap-8 max-w-[1450px] mx-auto bg-red-900/30 px-5 sm:px-8 md:px-10 py-3 md:py-3">
          <div className="bg-green-900/100 p-5 md:p-6 text-center shadow-md border border-green-800/40 w-full max-w-[260px] sm:max-w-[280px] md:max-w-[350px] mx-auto">
            <div className="text-4xl md:text-5xl font-extrabold text-white">{stats.finished}</div>
            <div className="mt-2 text-lg md:text-xl font-semibold text-white">Finished Projects</div>
          </div>
          <div className="bg-[#5d6436] p-5 md:p-6 text-center shadow-md border border-green-800/40 w-full max-w-[260px] sm:max-w-[280px] md:max-w-[350px] mx-auto">
            <div className="text-4xl md:text-5xl font-extrabold text-white">{stats.ongoing}</div>
            <div className="mt-2 text-lg md:text-xl font-semibold text-white">Ongoing Projects</div>
          </div>
          <div className="bg-red-900/70 p-5 md:p-6 text-center shadow-md border border-green-800/40 w-full max-w-[260px] sm:max-w-[280px] md:max-w-[350px] mx-auto">
            <div className="text-4xl md:text-5xl font-extrabold text-white">{stats.overdue}</div>
            <div className="mt-2 text-lg md:text-xl font-semibold text-white">Overdue Projects</div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex flex-col md:flex-row flex-1">
        {/* Desktop sidebar */}
        <aside
          className="hidden md:block w-72 lg:w-60 p-4 bg-gray-950 border-r border-gray-800 sticky top-0 h-screen overflow-y-auto"
        >
          <div className="pt-1 pb-4">
            <FilterContent
              draftFilters={draftFilters}
              setDraftFilters={setDraftFilters}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              filterFields={filterFields}
              getUniqueValues={getUniqueValues}
              resetFilters={resetFilters}
              isApplyDisabled={isApplyDisabled}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
        </aside>

        {/* Mobile filter */}
        <div className="md:hidden bg-gray-950 border-b border-gray-800 px-4 py-4">
          <FilterContent
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            filterFields={filterFields}
            getUniqueValues={getUniqueValues}
            resetFilters={resetFilters}
            isApplyDisabled={isApplyDisabled}
            hasActiveFilters={hasActiveFilters}
          />
        </div>

        <main className="flex-1 flex flex-col min-h-0 md:h-screen md:overflow-hidden">
          {/* Results header + chips */}
          <div className="px-4 sm:px-6 py-2 bg-gray-900/70 border-b border-gray-700">
            <div className="flex flex-wrap items-center">
              <div className="flex items-center mr-1">
                <span className="px-3 py-1 text-xl font-bold text-blue-500 rounded-md">
                  {hasActiveFilters ? filteredData.length : totalProjects}
                </span>
                <span className="text-base font-medium text-blue-500 whitespace-nowrap">
                  {filtersAreApplied ? 'results found on this data' : 'total results'}
                </span>
              </div>

              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mt-2 md:mt-0 md:ml-4">
                  {Object.entries(draftFilters).map(([key, value]) => {
                    if (!value?.trim()) return null;

                    const label =
                      key === 'customerBrand' ? 'Customer Brand' :
                      key === 'productCategory' ? 'Product Category' :
                      key === 'level' ? 'Level' :
                      key === 'projectRanking' ? 'Project Ranking' :
                      key === 'supplier' ? 'Supplier' :
                      key === 'status' ? 'Status' :
                      key === 'certificationlogo' ? 'Certification logo' :
                      key === 'searchKeyword' ? 'Keyword' : key;

                    return (
                      <div
                        key={key}
                        className="inline-flex items-center gap-1.5 bg-blue-600/90 hover:bg-blue-700 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-all shadow-sm"
                      >
                        <span>
                          <span className="font-semibold">{label}:</span> {value.trim()}
                        </span>
                        <button
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, [key]: '' }));
                            setAppliedFilters((prev) => ({ ...prev, [key]: '' }));
                          }}
                          className="ml-1 text-white/90 hover:text-white rounded-full p-1 hover:bg-white/20 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* TABLE */}
         <div
            ref={topScrollRef}
            onScroll={syncTopScroll}
            className="overflow-x-scroll overflow-y-hidden h-5 custom-scrollbar"
          >
            <div className="w-[1400px] h-[1px]" />
          </div>

          {/* TABLE */}
          <div
            ref={tableScrollRef}
            onScroll={syncTableScroll}
            className="flex-1 overflow-x-scroll overflow-y-auto min-h-0 custom-scrollbar"
          >
              <table className="w-full min-w-[1400px] text-sm border-collapse border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-gray-600 backdrop-blur-sm border-b border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Customer Brand</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Customer Model Name</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Product Category</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Level</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Project Ranking</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Supplier</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">KO Date</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">MP1 Deadline</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Schedule Plan Status</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Tracking</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Status</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">HDMI</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">DP</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">USB IF</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">typeC 相容性</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">LBL</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">DTS</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">HDR</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Clear MR</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">TBT4</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Calman Ready</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Flicker Free</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">MS Dynamic lighting</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">Hello Webcam</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap border border-gray-800">圆偏光</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-800 bg-black/40">
                {filteredData.length > 0 ? (
                  filteredData.map((item, idx) =>
                    item && item.customerBrand ? (
                      <tr
                        key={idx}
                        className={`${
                          idx % 2 === 0 ? 'bg-white' : 'bg-white'
                        } hover:bg-gray-50 transition-colors text-black text-sm font-semibold`}
                      >
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.customerBrand}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.customerModel || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.productCategory || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.level || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.projectRanking || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.supplier || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.koDate || '-'}
                        </td>
                        <td
                          className={`px-4 py-3 text-left border border-gray-800 font-bold whitespace-nowrap ${
                            item.isOverdue ? 'bg-red-600 text-white' : ''
                          }`}
                        >
                          {item.mp1Deadline || '-'}
                        </td>
                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.scheduleStatus || '-'}
                        </td>

                        <td className="px-0 py-3 text-left border-b border-gray-800 whitespace-nowrap">
                          {item.plan && item.actual ? (
                            <div className="flex flex-col text-left">
                              <div className="px-4 whitespace-nowrap">Plan</div>
                              <div className="w-full border-t border-black my-1"></div>
                              <div className="px-4 whitespace-nowrap">Actual</div>
                            </div>
                          ) : item.plan ? (
                            <div className="px-4 whitespace-nowrap">Plan</div>
                          ) : item.actual ? (
                            <div className="px-4 whitespace-nowrap">Actual</div>
                          ) : (
                            <div className="px-4 whitespace-nowrap">-</div>
                          )}
                        </td>

                        <td className="px-4 py-3 text-left border border-gray-800 whitespace-nowrap">
                          {item.plan?.status && item.actual?.status ? (
                            item.plan.status === item.actual.status
                              ? String(item.plan.status)
                              : String(item.actual.status)
                          ) : item.plan?.status ? (
                            String(item.plan.status)
                          ) : item.actual?.status ? (
                            String(item.actual.status)
                          ) : (
                            '-'
                          )}
                        </td>
                        {[
                          'hdmi',
                          'dp',
                          'usbIF',
                          'typeC',
                          'lbl',
                          'dts',
                          'hdr',
                          'clearMR',
                          'tbt4',
                          'calmanReady',
                          'flickerFree',
                          'msDynamic',
                          'helloWebcam',
                          'circularPolarizer',
                        ].map((cert) => {
                          const val = item.plan?.[cert];
                          const hasTwo = val && val.includes('\n');
                          if (hasTwo) {
                            const [top, bot] = val.split('\n');
                            return (
                              <td key={cert} className="px-0 py-3 text-left border border-gray-800 whitespace-nowrap">
                                <div className="flex flex-col whitespace-nowrap">
                                  <div className="px-4">{top}</div>
                                  <div className="w-full border-t border-gray-500 my-1"></div>
                                  <div className="px-4">{bot}</div>
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td key={cert} className="px-0 py-3 text-left border border-gray-800 whitespace-nowrap">
                              {val ? <div className="px-4">{val}</div> : <div className="px-4">-</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ) : null
                  )
                ) : (
                  <tr>
                    <td colSpan={25} className="px-4 py-8 text-center text-gray-400 whitespace-nowrap">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>

      {/* PAGINATION */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-950/90 backdrop-blur-md border-t border-gray-800 px-3 py-3 sm:px-4">
        <div className="flex items-center justify-center mx-auto w-full max-w-5xl">
          <div className="flex items-center gap-1 sm:gap-2 min-w-[260px] sm:min-w-[380px] justify-center flex-wrap">
            {totalPages <= 1 ? (
              <span className="text-gray-400 text-sm sm:text-base px-4 py-2 rounded bg-gray-800/50">
                Showing all results
              </span>
            ) : (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-50 transition-colors shrink-0"
                >
                  <ChevronsLeft size={18} />
                </button>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-50 transition-colors shrink-0"
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="flex items-center justify-center gap-1 sm:gap-1.5 min-w-[100px] sm:min-w-[180px] text-sm sm:text-base font-medium">
                  {getVisiblePageButtons()}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-50 transition-colors shrink-0"
                >
                  <ChevronRight size={18} />
                </button>

                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-50 transition-colors shrink-0"
                >
                  <ChevronsRight size={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="h-20 md:h-16" aria-hidden="true" />
    </div>
  );
};

export default MonitorControlTable;